import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { readMemory } from "../../../api/threads/memoriesApi";
import type { HumanStepRequestedPayload, ThreadEntry } from "../../../api/threads/liveMessages";
import { answerHumanStep, newClientKey } from "../../../api/threads/threadsApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useLoad } from "../../../shared/loading/useLoad";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import { FORM_COMPACT_CONTROL_CLASS, FORM_HELP_TEXT_CLASS } from "../../../ui/styles/formStyles";
import { responseDefinition, reviewedMemoryOf, reviewPartOf } from "../../threads/entries/humanStepResponse";
import { parseThreadEntryRef } from "../../../shared/references/threadReferences";
import PassageList from "../keep/PassageList";
import { notifyMemoriesChanged, onMemoriesChanged } from "../memoryEvents";
import MemoryWording from "./MemoryWording";

interface ReviewMemoryStepProps {
  readonly threadId: string;
  readonly projectId: string;
  readonly entry: ThreadEntry;
  readonly payload: HumanStepRequestedPayload;
}

/** The answers of a memory review step: its part `choice` (ADR-0031 D5). */
type MemoryDecision = "confirm" | "discard";

/**
 * A memory's review step (ADR-0031 D5, MEM-5): the proposed memory and the passages it
 * quotes, its wording editable by whoever may edit it, and Confirm or Discard for someone who may approve, through the
 * memories service. The server decides who may; the buttons follow what the memory says.
 */
export default function ReviewMemoryStep({ threadId, projectId, entry, payload }: ReviewMemoryStepProps) {
  const { t } = useTranslation();
  const memoryId = reviewedMemoryOf(payload);
  // The common review part's comment rule (proposal 0.8, O1): with it, the decision carries a comment.
  const commentRequired = reviewPartOf(responseDefinition(payload.response))?.commentRequired === true;
  const [comment, setComment] = useState("");
  const load = useCallback((signal: AbortSignal) => (memoryId === null ? Promise.resolve(null) : readMemory(projectId, memoryId, signal)), [projectId, memoryId]);
  const { state, reload, refresh } = useLoad(load);
  // An edit of this memory elsewhere (its Thread's list, the other pane) shows here before a decision.
  useEffect(() => onMemoriesChanged(refresh), [refresh]);
  const { state: answer, submit } = useSubmission();
  if (memoryId === null) return <p className={FORM_HELP_TEXT_CLASS}>{t("memories.step.noMemory")}</p>;
  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} />;
  const memory = state.value;
  if (memory === null) return null;
  const busy = answer.status === "submitting" || answer.status === "succeeded";
  const blocked = busy || (commentRequired && comment.trim() === "");
  const decide = (choice: MemoryDecision) => {
    submit(async () => {
      await answerHumanStep(threadId, { answersEntry: entry.number, comment: comment.trim() === "" ? null : comment.trim(), choice, result: null, clientRequestId: newClientKey() });
      notifyMemoriesChanged();
    });
  };
  return (
    <div className="space-y-3">
      <MemoryWording projectId={projectId} memory={memory} onChanged={refresh} />
      <PassageList full={true} passages={memory.sources.map((source) => ({ key: `${source.entry}:${source.text}`, quote: source.text, entry: parseThreadEntryRef(source.entry)?.number ?? source.entry }))} />
      {memory.mayConfirm ? (
        <div className="space-y-2">
          {commentRequired ? (
            <textarea
              className={FORM_COMPACT_CONTROL_CLASS}
              rows={2}
              value={comment}
              aria-label={t("threads.humanStep.commentRequired")}
              placeholder={t("threads.humanStep.commentRequired")}
              onChange={(event) => { setComment(event.target.value); }}
            />
          ) : null}
          <div className="flex flex-wrap gap-2">
            <AppButton variant="primary" size="sm" disabled={blocked} onClick={() => { decide("confirm"); }}>{t("memories.action.confirm")}</AppButton>
            <AppButton variant="secondary" size="sm" disabled={blocked} onClick={() => { decide("discard"); }}>{t("memories.action.discard")}</AppButton>
          </div>
        </div>
      ) : (
        <p className={FORM_HELP_TEXT_CLASS}>{t("memories.step.waitsForOwner")}</p>
      )}
      {answer.status === "failed" ? <ErrorNotice error={answer.error} compact={true} /> : null}
    </div>
  );
}
